import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatOdds, useOddsFormat } from "@/lib/odds";
import { useSheet } from "../ui/SheetHost";
import CashOutSheet from "../sheets/CashOutSheet";

/* MobileMyBets (design 4a + 4b) — the My Bets tab root.
   Open: leg-by-leg status (won / live / pending), a full-width cash-out button
   carrying the backend value, and an honest "Cash out unavailable · market
   suspended" lock when the backend says so. Settled: dense rows (won / lost /
   cashed) with per-currency marks — PCoins and PCash results NEVER blend; each
   bet renders with its OWN currency.

   REAL data: GET /bets/me + GET /parlays/me (polled 5s), and the cash-out quote
   from GET /bets|parlays/:id/cashout. Cash-out confirm happens in CashOutSheet
   (POST /bets|parlays/:id/cashout, idempotent, re-asks on value move). */

const fmtNum = (amt, currency) => currency === "cash"
  ? `$${(Number(amt) || 0).toFixed(2)}`
  : (Number(amt) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isSettled = (s) => ["won", "lost", "push", "refunded", "cashed_out"].includes(s);

// Amount + its own currency mark. Coins → green pnrcoin; cash → silver pnrcash. Never blended.
function Amt({ value, currency, size = 14, color }) {
  const cash = currency === "cash";
  return (
    <span className="m-tnum" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: color || "#fff" }}>
      <Icon name={cash ? "pnrcash" : "pnrcoin"} size={size} style={{ color: cash ? "#E4E4E7" : "#00D632", flex: "none" }} />
      {fmtNum(value, currency)}
    </span>
  );
}

const SETTLED_PILL = {
  won: { label: "Won", bg: "rgba(0,214,50,.14)", fg: "#00D632", vc: "#00D632", sign: "+" },
  lost: { label: "Lost", bg: "rgba(239,68,68,.14)", fg: "#EF4444", vc: "#EF4444", sign: "-" },
  push: { label: "Push", bg: "#1C1C21", fg: "#a1a1aa", vc: "#fff", sign: "" },
  refunded: { label: "Refunded", bg: "#1C1C21", fg: "#a1a1aa", vc: "#fff", sign: "" },
  cashed_out: { label: "Cashed out", bg: "#1C1C21", fg: "#a1a1aa", vc: "#fff", sign: "" },
};

export default function MobileMyBets() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const oddsFmt = useOddsFormat();
  const { openSheet } = useSheet();
  const [bets, setBets] = useState(null);
  const [parlays, setParlays] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [pQuotes, setPQuotes] = useState({});
  const [loadErr, setLoadErr] = useState(false);
  const [tab, setTab] = useState("open");
  const [chip, setChip] = useState("all");

  const loadBets = useCallback(async () => {
    try {
      const [b, p] = await Promise.all([api.get("/bets/me"), api.get("/parlays/me")]);
      setBets(b.data); setParlays(p.data); setLoadErr(false);
      return { bets: b.data, parlays: p.data };
    } catch {
      setLoadErr(true);
      return { bets: bets || [], parlays: parlays || [] };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuotes = useCallback(async (data) => {
    const pending = (data?.bets || []).filter((b) => b.status === "pending");
    const entries = await Promise.all(pending.map(async (b) => {
      try { const { data } = await api.get(`/bets/${b.id}/cashout`); return [b.id, data]; }
      catch { return [b.id, { available: false }]; }
    }));
    setQuotes(Object.fromEntries(entries));
    const pPending = (data?.parlays || []).filter((p) => p.status === "pending");
    const pEntries = await Promise.all(pPending.map(async (p) => {
      try { const { data } = await api.get(`/parlays/${p.id}/cashout`); return [p.id, data]; }
      catch { return [p.id, { available: false }]; }
    }));
    setPQuotes(Object.fromEntries(pEntries));
  }, []);

  useEffect(() => {
    loadBets().then(loadQuotes);
    const t = setInterval(async () => { const d = await loadBets(); await loadQuotes(d); }, 5000);
    return () => clearInterval(t);
  }, [loadBets, loadQuotes]);

  // Unify bets + parlays into one card model (mirrors the desktop MyBets model).
  const cards = useMemo(() => {
    const out = [];
    (parlays || []).forEach((p) => out.push({
      id: p.id, kind: "parlay", legs: p.legs || [], status: p.status, currency: p.currency, stake: p.stake,
      createdAt: p.created_at, price: p.combined_odds, potentialPayout: p.potential_payout, payout: p.payout,
      cashoutValue: p.cashout_value != null ? p.cashout_value : p.payout, inPlay: !!p.in_play,
      cashQuote: pQuotes[p.id], endpoint: "parlays", _t: p.created_at,
    }));
    (bets || []).forEach((b) => out.push({
      id: b.id, kind: "straight",
      legs: [{ event_label: b.event_label, outcome_label: b.outcome_label, odds: b.odds, status: b.status === "pending" ? "pending" : b.status }],
      status: b.status, currency: b.currency, stake: b.stake, createdAt: b.created_at,
      price: b.odds, potentialPayout: b.potential_payout, payout: b.potential_payout, cashoutValue: b.cashout_value,
      inPlay: !!b.in_play, cashQuote: quotes[b.id], endpoint: "bets", _t: b.created_at,
    }));
    out.sort((a, b) => new Date(b._t) - new Date(a._t));
    return out;
  }, [bets, parlays, quotes, pQuotes]);

  const openCards = cards.filter((c) => c.status === "pending");
  const settledCards = cards.filter((c) => isSettled(c.status));
  const chipMatch = (c) => chip === "all" || (chip === "push" ? (c.status === "push" || c.status === "refunded") : c.status === chip);
  const shown = tab === "open" ? openCards : settledCards.filter(chipMatch);

  const openCashOut = (card) => {
    openSheet(({ close }) => (
      <CashOutSheet close={close} bet={card} onDone={async () => { const d = await loadBets(); await loadQuotes(d); await refresh(); }} />
    ), { detent: "medium", label: "Cash out" });
  };

  const settledCount = (s) => s === "push"
    ? settledCards.filter((c) => c.status === "push" || c.status === "refunded").length
    : settledCards.filter((c) => c.status === s).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "14px 16px 0" }}>
      <h1 style={{ font: "800 28px 'Manrope'", letterSpacing: "-.035em" }}>My Bets</h1>

      {/* Open / Settled segmented */}
      <div style={{ display: "flex", height: 44, borderRadius: 14, padding: 4, background: "#0E0E11", boxShadow: "inset 0 0 0 1px #1F1F23,inset 0 1px 2px rgba(0,0,0,.4)" }}>
        {[["open", "Open", openCards.length], ["settled", "Settled", settledCards.length]].map(([id, label, n]) => {
          const active = tab === id;
          return (
            <button key={id} type="button" onClick={() => setTab(id)}
              style={{ flex: 1, borderRadius: 10, border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, font: active ? "700 14px 'Outfit'" : "600 14px 'Outfit'", color: active ? "#fff" : "#71717a",
                background: active ? "linear-gradient(180deg,#2A2A30,#202025)" : "transparent", boxShadow: active ? "inset 0 1px 0 rgba(255,255,255,.1),0 2px 6px rgba(0,0,0,.4)" : "none" }}>
              {label}<span className="m-tnum" style={{ font: "700 12px 'Manrope'", color: active ? "#71717a" : "#52525b" }}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* settled filter chips */}
      {tab === "settled" && (
        <div className="m-no-scrollbar" style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
          {[["all", "All", settledCards.length], ["won", "Won", settledCount("won")], ["lost", "Lost", settledCount("lost")], ["cashed_out", "Cashed out", settledCount("cashed_out")]].map(([id, label, n]) => {
            const active = chip === id;
            return (
              <button key={id} type="button" onClick={() => setChip(id)} aria-pressed={active}
                style={{ flex: "none", height: 34, borderRadius: 999, padding: "0 14px", border: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  background: active ? "#F4F4F5" : "#18181C", boxShadow: active ? "none" : "inset 0 0 0 1px #26262B", color: active ? "#0A0A0A" : "#a1a1aa", font: active ? "700 13px 'Outfit'" : "600 13px 'Outfit'" }}>
                {label}<span className="m-tnum" style={{ font: "700 11px 'Manrope'", color: active ? "#52525b" : "#71717a" }}>{n}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* states */}
      {bets === null && !loadErr && <Skeleton />}
      {loadErr && (
        <div style={{ textAlign: "center", padding: "16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
          <p style={{ color: "#a1a1aa", font: "500 13px 'Outfit'" }}>We couldn’t load your bets.</p>
          <button type="button" onClick={() => loadBets().then(loadQuotes)} style={{ height: 40, borderRadius: 12, padding: "0 16px", border: 0, cursor: "pointer", background: "#18181C", boxShadow: "inset 0 0 0 1px #2F2F35", color: "#fff", font: "700 13px 'Outfit'" }}>Try again</button>
        </div>
      )}

      {bets !== null && shown.length === 0 && !loadErr && (
        <div style={{ textAlign: "center", padding: "36px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
          <span style={{ width: 64, height: 64, borderRadius: 20, background: "#141417", boxShadow: "inset 0 0 0 1px #26262B", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="mybets" size={28} style={{ color: "#52525b" }} /></span>
          <p style={{ color: "#71717a", font: "500 13px 'Outfit'", maxWidth: 260 }}>
            {tab === "open" ? "No open bets. Picks you place will show here while they’re live." : chip === "all" ? "No settled bets yet." : `No ${chip === "cashed_out" ? "cashed out" : chip} bets yet.`}
          </p>
          {tab === "open" && <button type="button" onClick={() => navigate("/sportsbook")} style={{ height: 44, borderRadius: 14, padding: "0 18px", border: 0, cursor: "pointer", background: "#18181C", boxShadow: "inset 0 0 0 1px #2F2F35", color: "#fff", font: "700 14px 'Outfit'" }}>Browse the board</button>}
        </div>
      )}

      {/* cards */}
      {tab === "open"
        ? shown.map((c) => <OpenCard key={`${c.kind}-${c.id}`} card={c} oddsFmt={oddsFmt} onCash={() => openCashOut(c)} />)
        : shown.map((c) => <SettledRow key={`${c.kind}-${c.id}`} card={c} oddsFmt={oddsFmt} />)}
    </div>
  );
}

function OpenCard({ card, oddsFmt, onCash }) {
  const { kind, legs, currency, stake, price, potentialPayout, cashQuote, inPlay } = card;
  const canCash = cashQuote && cashQuote.available && cashQuote.value != null;
  const cashUnavailable = cashQuote && cashQuote.available === false;

  return (
    <div style={{ borderRadius: 20, overflow: "hidden", background: "linear-gradient(180deg,#17171B 0%,#111114 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06),inset 0 1px 0 rgba(255,255,255,.09),0 12px 28px rgba(0,0,0,.35)" }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px 0" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {kind === "parlay" && <Icon name="layers" size={16} style={{ color: "#a1a1aa" }} />}
          <span style={{ font: "700 14px 'Manrope'" }}>{kind === "parlay" ? `${legs.length}-leg parlay` : "Straight"}</span>
          <span className="m-tnum" style={{ font: "800 14px 'Manrope'", color: "#fff" }}>{formatOdds(price, oddsFmt)}</span>
        </span>
        {inPlay && (
          <span style={{ display: "flex", alignItems: "center", gap: 5, padding: "2px 7px", borderRadius: 6, background: "rgba(239,68,68,.14)", font: "800 10px 'Outfit'", letterSpacing: ".1em", color: "#EF4444" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#EF4444", animation: "pnrPulse 1.6s ease-out infinite" }} />LIVE
          </span>
        )}
      </div>
      {/* legs */}
      <div style={{ padding: "8px 14px 0" }}>
        {legs.map((l, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 50, borderTop: i > 0 ? "1px solid #1A1A1E" : "none" }}>
            <LegDot status={l.status} />
            <span style={{ flex: 1, minWidth: 0, padding: "6px 0" }}>
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", font: "700 14px 'Outfit'", color: "#fff" }}>{l.outcome_label}</span>
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", font: "500 11px 'Outfit'", color: "#71717a" }}>{l.event_label}</span>
            </span>
            <span className="m-tnum" style={{ flex: "none", font: "800 13px 'Manrope'", color: "#a1a1aa" }}>{formatOdds(l.odds, oddsFmt)}</span>
          </div>
        ))}
      </div>
      {/* cash out */}
      <div style={{ padding: "10px 14px 0" }}>
        {canCash ? (
          <button type="button" onClick={onCash} style={{ width: "100%", height: 48, borderRadius: 14, border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "#F4F4F5", color: "#0A0A0A", font: "800 14px 'Manrope'" }}>
            Cash out <Amt value={cashQuote.value} currency={currency} size={14} color="#0A0A0A" />
          </button>
        ) : cashUnavailable ? (
          <span style={{ width: "100%", height: 44, borderRadius: 14, background: "#141417", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, font: "600 13px 'Outfit'", color: "#71717a" }}>
            <Icon name="lock" size={14} style={{ color: "#71717a" }} /> Cash out unavailable · market suspended
          </span>
        ) : null}
      </div>
      {/* footer */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 12, padding: "12px 14px", background: "#0F0F12", borderTop: "1px solid #1A1A1E" }}>
        <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ font: "600 10px 'Outfit'", letterSpacing: ".1em", textTransform: "uppercase", color: "#71717a" }}>Stake</span>
          <Amt value={stake} currency={currency} size={14} />
        </span>
        <span style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
          <span style={{ font: "600 10px 'Outfit'", letterSpacing: ".1em", textTransform: "uppercase", color: "#71717a" }}>To return</span>
          <span style={{ font: "800 18px 'Manrope'" }}><Amt value={potentialPayout} currency={currency} size={16} /></span>
        </span>
      </div>
    </div>
  );
}

function SettledRow({ card, oddsFmt }) {
  const { kind, legs, status, currency, stake, price } = card;
  const p = SETTLED_PILL[status] || SETTLED_PILL.push;
  const value = status === "won" ? card.payout : status === "lost" ? stake : status === "cashed_out" ? card.cashoutValue : stake;
  const pick = legs[0]?.outcome_label || (kind === "parlay" ? `${legs.length}-leg parlay` : "Bet");
  const meta = kind === "parlay" ? `${legs.length} legs · ${formatOdds(price, oddsFmt)}` : (legs[0]?.event_label || "");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 72, padding: "12px 14px", borderRadius: 14, background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
      <span style={{ flex: "none", padding: "3px 8px", borderRadius: 999, background: p.bg, color: p.fg, font: "700 10px 'Outfit'", letterSpacing: ".04em", textTransform: "uppercase", alignSelf: "flex-start" }}>{p.label}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", font: "700 14px 'Outfit'", color: "#fff" }}>{pick}</span>
        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", font: "500 11.5px 'Outfit'", color: "#71717a" }}>{meta}</span>
      </span>
      <span style={{ flex: "none", font: "800 15px 'Manrope'" }}>
        <span className="m-tnum" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: p.vc }}>
          {p.sign && <span>{p.sign}</span>}
          <Icon name={currency === "cash" ? "pnrcash" : "pnrcoin"} size={14} style={{ color: currency === "cash" ? "#E4E4E7" : "#00D632" }} />
          {fmtNum(value, currency)}
        </span>
      </span>
    </div>
  );
}

function LegDot({ status }) {
  if (status === "won") return <span style={{ width: 20, height: 20, flex: "none", borderRadius: "50%", background: "#00D632", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="check" size={11} style={{ color: "#0A0A0A" }} /></span>;
  if (status === "lost") return <span style={{ width: 20, height: 20, flex: "none", borderRadius: "50%", boxShadow: "inset 0 0 0 2px #EF4444", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="x" size={11} style={{ color: "#EF4444" }} /></span>;
  const ring = status === "live" ? "#EF4444" : status === "push" ? "#52525b" : "#3F3F46";
  return <span style={{ width: 20, height: 20, flex: "none", borderRadius: "50%", boxShadow: `inset 0 0 0 2px ${ring}`, position: "relative" }}>
    {status === "live" && <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 7, height: 7, borderRadius: "50%", background: "#EF4444", animation: "pnrPulse 1.6s ease-out infinite" }} />}
  </span>;
}

function Skeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {[0, 1].map((i) => <div key={i} style={{ height: 150, borderRadius: 20, background: "#141417", boxShadow: "inset 0 0 0 1px #1F1F23" }} />)}
    </div>
  );
}
